import type { PrescriptionInfo, ProductInfo } from '@/components/products/refill/ProductRefillCard';
import type { UnavailableProductIssue } from '@/components/products/refill/UnavailableProductRefillCard';
import settings from '@/constants/constants';
import type {
  CartableProduct,
  Device,
  ListProductsResponse,
  Match,
  Medication,
  Product,
  ProductStrain,
  ProductFormulation as ShopifyProductFormulation,
} from '@/hooks/escript/types';
import {
  IssueStatuses,
  ProductStrains,
  ProductFormulations as ShopifyProductFormulations,
} from '@/hooks/escript/types';
import { deriveMaxAllowedQuantity } from '@/hooks/escript/util';
import { ESCRIPT_REFILL_ROUTE } from '@/routes/route-paths';
import { PRODUCT_ISSUE_TEXT } from '@montugroup/circuit-shared';
import type { ProductFormulation } from '@montugroup/circuit-shared/types/index';
import { ProductFormulations } from '@montugroup/circuit-shared/types/index';
import React from 'react';
import { Link } from 'react-router-dom';

export const UNKNOWN_SUPPLIER_NAME = 'Unknown Supplier';

export const PRODUCT_PURCHASE_ISSUE_TEXT = {
  supportPhone: settings.support.phone.display,

  prescriptionExpired: {
    title: PRODUCT_ISSUE_TEXT.PRESCRIPTION_EXPIRED,
    info: ['Book a follow up with your doctor'],
    linkTo: '/patient/consultations',
  },

  outOfStock: {
    title: PRODUCT_ISSUE_TEXT.OUT_OF_STOCK,
    info: ['Please consult your doctor to discuss any alternatives.'],
  },

  intervalPeriod: {
    title: PRODUCT_ISSUE_TEXT.DISPENSE_INTERVAL,
    defaultInfo: ['You have ordered the maximum prescribed quantity of this product for your interval period.'],
  },

  discontinued: {
    title: PRODUCT_ISSUE_TEXT.DISCONTINUED,
    info: ["Please select 'Request replacement' to notify your doctor for a replacement product."],
  },
  infoRequestAlternate: "Please select 'Request alternative product' to notify your doctor for a replacement product.",
  scriptInReviewApprovalLetter: {
    title: PRODUCT_ISSUE_TEXT.SCRIPT_IN_REVIEW,
    info: [
      "We're verifying your script to make sure it matches the product in our catalogue. Please check again later.",
      React.createElement(
        React.Fragment,
        null,
        'You can upload your ',
        React.createElement(
          'a',
          {
            href: 'https://support.umeds.com.au/hc/en-au/articles/10869503095695-Regulations-and-approvals',
            target: '_blank',
            className: 'underline',
          },
          'approval letter',
        ),
        ' now for a seamless order.',
      ),
    ],
  },
  scriptInReviewVerifyScript: {
    title: PRODUCT_ISSUE_TEXT.SCRIPT_IN_REVIEW,
    info: [
      "We're verifying your script to make sure it matches the product in our catalogue. Please check again later.",
    ],
  },
  scriptUnavailable: {
    title: 'Unavailable',
    info: ['Your prescription is ineligible.'],
  },
  pendingDispense: {
    title: PRODUCT_ISSUE_TEXT.PENDING_DISPENSE,
    info: ["Sit tight, we're dispensing this medication for you."],
  },
  consumed: {
    title: PRODUCT_ISSUE_TEXT.CONSUMED,
    info: [
      React.createElement(
        React.Fragment,
        null,
        'To access your next repeat, ',
        React.createElement(
          Link,
          {
            to: ESCRIPT_REFILL_ROUTE,
            state: { addNewScript: true },
            className: 'underline',
          },
          'add script token',
        ),
      ),
    ],
  },
};

export const getProductPurchaseIssue = (
  product: CartableProduct,
  isDevice?: boolean,
): UnavailableProductIssue | null => {
  const status = product.productIssueStatus;

  switch (status) {
    case IssueStatuses.OUT_OF_STOCK:
      return {
        title: PRODUCT_PURCHASE_ISSUE_TEXT.outOfStock.title,
        info: !isDevice ? PRODUCT_PURCHASE_ISSUE_TEXT.outOfStock.info : [],
        isHighSeverity: true,
      };

    case IssueStatuses.PRESCRIPTION_EXPIRED:
      return {
        title: PRODUCT_PURCHASE_ISSUE_TEXT.prescriptionExpired.title,
        info: PRODUCT_PURCHASE_ISSUE_TEXT.prescriptionExpired.info,
        isHighSeverity: false,
      };

    case IssueStatuses.DISPENSE_INTERVAL:
      if ('intervalDetails' in product && product.intervalDetails) {
        // TODO: Update dispense interval once interval time work has been completed.
        const defaultInfoText = PRODUCT_PURCHASE_ISSUE_TEXT.intervalPeriod.defaultInfo;
        return {
          title: PRODUCT_PURCHASE_ISSUE_TEXT.intervalPeriod.title,
          info: defaultInfoText,
          isHighSeverity: false,
        };
      }
      break;

    case IssueStatuses.DISCONTINUED:
      return {
        title: PRODUCT_PURCHASE_ISSUE_TEXT.discontinued.title,
        info: [],
        isHighSeverity: true,
      };

    case IssueStatuses.CONSUMED:
      return {
        title: PRODUCT_PURCHASE_ISSUE_TEXT.consumed.title,
        info: PRODUCT_PURCHASE_ISSUE_TEXT.consumed.info,
        isHighSeverity: true,
      };

    case IssueStatuses.SCRIPT_IN_REVIEW:
      return {
        title: PRODUCT_PURCHASE_ISSUE_TEXT.scriptInReviewVerifyScript.title,
        info: PRODUCT_PURCHASE_ISSUE_TEXT.scriptInReviewVerifyScript.info,
        isHighSeverity: false,
      };

    default:
      break;
  }

  return null;
};

export const mapToProductRefillCardPrescription = (prescribedProduct: Medication): PrescriptionInfo => ({
  expiry: prescribedProduct.expiry,
  maxQuantity: prescribedProduct ? deriveMaxAllowedQuantity(prescribedProduct) : 0,
  maxRepeats: prescribedProduct ? prescribedProduct.repeats || 0 : 0,
  interval: prescribedProduct.intervalDetails?.interval || 0,
});

function resolveStrainName(strain: string | null | undefined) {
  if (!strain) {
    return null;
  }

  const productStrain = strain as ProductStrain;

  switch (productStrain) {
    case ProductStrains.HYBRID:
      return 'Hybrid';
    case ProductStrains.SATIVA:
      return 'Sativa';
    case ProductStrains.INDICA:
      return 'Indica';
  }
}

function mapFormulationToLegacy(formulation: string): ProductFormulation {
  const productFormulation = formulation as ShopifyProductFormulation;

  switch (productFormulation) {
    case ShopifyProductFormulations.CAPSULE:
      return ProductFormulations.CAPSULE;
    case ShopifyProductFormulations.CONCENTRATED_EXTRACT:
      return ProductFormulations.CONCENTRATED_EXTRACT;
    case ShopifyProductFormulations.HERB_DRIED:
      return ProductFormulations.HERB_DRIED;
    case ShopifyProductFormulations.INHALANT_PRESSURE:
      return ProductFormulations.INHALANT_PRESSURIZED;
    case ShopifyProductFormulations.INHALATION:
      return ProductFormulations.INHALATION;
    case ShopifyProductFormulations.ORAL_LIQUID:
      return ProductFormulations.ORAL_LIQUID;
    case ShopifyProductFormulations.PASTILLE:
      return ProductFormulations.PASTILLE;
    case ShopifyProductFormulations.POWDER:
      return ProductFormulations.POWDER;
    case ShopifyProductFormulations.SPRAY:
      return ProductFormulations.SPRAY;
    case ShopifyProductFormulations.TABLET_CHEWABLE:
      return ProductFormulations.TABLET_CHEWABLE;
    case ShopifyProductFormulations.TOPICAL:
      return ProductFormulations.TOPICAL;
    case ShopifyProductFormulations.WAFER:
      return ProductFormulations.WAFER;
    default:
      return ProductFormulations.DEVICE;
  }
}

export const mapToProductRefillCardProduct = (prescribedProduct: Medication): ProductInfo => ({
  id: prescribedProduct.id,
  cbdForDisplay: prescribedProduct.cbdStrength || '',
  thcForDisplay: prescribedProduct.thcStrength || '',
  formulation: mapFormulationToLegacy(prescribedProduct.formulation ?? ''),
  price: prescribedProduct.price ?? 0,
  productName: prescribedProduct.name,
  strainName: resolveStrainName(prescribedProduct.strain),
  strainKey: prescribedProduct.strain ? (prescribedProduct.strain as ProductStrain) : null,
  supplier: prescribedProduct.supplier || UNKNOWN_SUPPLIER_NAME,
  size: prescribedProduct.size ?? null,
  isConcession: prescribedProduct.isConcession,
  prescriptionCode: prescribedProduct?.prescriptionCode ?? '',
});

export const mapDeviceToIncrementProductRefillCard = (device: Device): ProductInfo => ({
  id: device.id,
  cbdForDisplay: '',
  thcForDisplay: '',
  formulation: ProductFormulations.DEVICE,
  price: device.price ?? 0,
  productName: device.name,
  strainName: null,
  strainKey: null,
  supplier: device.supplier || UNKNOWN_SUPPLIER_NAME,
  size: null,
  isConcession: false,
});

export interface ProductIssuePair {
  product: CartableProduct;
  issue: UnavailableProductIssue | null;
}

// Status priority mapping is used to determine the order of statuses when sorted
// lower number is higher priority
const ISSUE_STATUS_PRIORITY: Record<keyof typeof IssueStatuses, number> = {
  NONE: 1,
  SCRIPT_IN_REVIEW: 2,
  DISPENSE_INTERVAL: 3,
  PENDING_DISPENSE: 4,
  DISPENSED: 5,
  OUT_OF_REPEATS: 6,
  PRESCRIPTION_EXPIRED: 7,
  OUT_OF_STOCK: 8,
  DISCONTINUED: 9,
  CONSUMED: 10,
};

export const sortProductIssues = (a: ProductIssuePair, b: ProductIssuePair) => {
  // Sort by issue status priority first
  const statusA = a.product.productIssueStatus;
  const statusB = b.product.productIssueStatus;
  const priorityDiff = (ISSUE_STATUS_PRIORITY[statusA] || 999) - (ISSUE_STATUS_PRIORITY[statusB] || 999);

  if (priorityDiff !== 0) {
    return priorityDiff;
  }

  // If same status, sort by issue title
  const titleA = a.issue?.title || '';
  const titleB = b.issue?.title || '';
  return titleA.localeCompare(titleB);
};

export const generateProductIssuePairs = (products: Product[] | Match[], isDevice: boolean): ProductIssuePair[] =>
  products.map((product) => {
    const issue = getProductPurchaseIssue(product, isDevice);
    return {
      product,
      issue,
    };
  });

export const getCantOrderProducts = (products: ProductIssuePair[]): ProductIssuePair[] => {
  const unavailableProductsWithIssues = products.filter((pip) => pip.issue !== null);
  return unavailableProductsWithIssues.sort(sortProductIssues);
};

export const getProductCategoryValues = (response?: ListProductsResponse) => {
  const productIssuePairs = generateProductIssuePairs(response?.medications ?? [], false);
  const availableProducts = productIssuePairs
    .filter((pip) => pip.issue === null)
    .map((pip) => pip.product as Medication);

  const deviceIssuePairs = generateProductIssuePairs(response?.devices ?? [], true);
  const otherProducts = deviceIssuePairs
    .filter((pip) => {
      return pip.issue === null;
    })
    .map((pip) => pip.product as Device);

  const cantOrderProducts = getCantOrderProducts(productIssuePairs);
  const cantOrderDevices = getCantOrderProducts(deviceIssuePairs);
  const productMatchIssues = generateProductIssuePairs(response?.productMatches ?? [], false);

  return {
    availableProducts,
    productMatchIssues,
    cantOrderProducts,
    cantOrderDevices,
    otherProducts,
  };
};
